import { AnswerForType, Enduser } from "@tellescope/types-models";
import { TellescopeFieldResponse } from "../../types";
import { TellescopeModelFromName, useTellescopeQueries } from "@tellescope/tanstack-query";
import { getLatestAddressAnswer, getLatestInsuranceAnswer } from "../../utils";
import { BRIDGE_TITLE } from "@tellescope/constants";
import { createKeyMap, hashString } from "@tellescope/utils";
import { useCallback, useRef, useState } from "react";

export type BridgeEligibilityPhase =
  | "idle"
  | "not-configured"
  | "loading-soft"
  | "polling-hard"
  | "error"
  | "result"
  | "cached";

export type BridgeServiceTypeResult = {
  status?: string;
  userIds?: string[];
};
interface BridgeResult {
  phase: BridgeEligibilityPhase;
  aggregateResult: {
    status: string | undefined;
    userIds: string[];
  };
}
interface BridgeServiceTypeResultWithId extends BridgeServiceTypeResult {
  serviceTypeId: string;
}

/**
 * A Bridge hard check is billed per call, so it must never be re-issued by an incidental refetch
 * trigger such as window focus or reconnect. `gcTime` matters as much as `staleTime` here: the field
 * is remounted on every wizard step change, and an evicted entry would be refetched — and rebilled —
 * on the way back.
 */
const PAID_CHECK_QUERY_OPTIONS = {
  staleTime: Infinity,
  gcTime: Infinity,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

/** Statuses that mean the payer has not answered yet, so the poll should run again. */
const PENDING_POLL_STATUSES = ["pending", "queued", "in_progress", "processing"];

const POLL_INTERVAL_MS = 2_000;

const isPendingPollStatus = (status: string | undefined) =>
  !!status && PENDING_POLL_STATUSES.includes(status.toLowerCase());

type ProxyReadRequest = {
  integration: string;
  type: string;
  id?: string;
  query?: string;
};

const getAggregateResult = (results: BridgeServiceTypeResultWithId[]) => {
  const allUserIds = new Set<string>();
  let eligibleStatus: string | undefined;
  let firstNonErrorStatus: string | undefined;
  results.forEach((result) => {
    result.userIds?.forEach((uid: unknown) => {
      if (typeof uid === "string") allUserIds.add(uid);
    });
    if (result.status === "ELIGIBLE") {
      eligibleStatus = "ELIGIBLE";
    } else if (result.status && result.status !== "error" && !firstNonErrorStatus) {
      firstNonErrorStatus = result.status;
    }
  });
  return { status: eligibleStatus ?? firstNonErrorStatus ?? "unknown", userIds: Array.from(allUserIds) };
};

const extractCheckResult = (responseData: unknown): BridgeServiceTypeResult | undefined => {
  if (typeof responseData !== "object" || responseData === null) return undefined;
  const status = "status" in responseData && typeof responseData.status === "string" ? responseData.status : undefined;
  const rawUserIds = "userIds" in responseData ? responseData.userIds : undefined;
  const userIds = Array.isArray(rawUserIds) ? rawUserIds.filter((uid): uid is string => typeof uid === "string") : [];
  return status ? { status, userIds } : undefined;
};

/**
 * Recovers which service type a proxy request was issued for, so a settled response can be attributed
 * without relying on the position of the request in the array.
 */
const getRequestedServiceTypeId = (request: unknown): string | undefined => {
  if (typeof request !== "object" || request === null || !("query" in request)) return undefined;
  if (typeof request.query !== "string") return undefined;

  const parsed: unknown = JSON.parse(request.query);
  if (typeof parsed !== "object" || parsed === null || !("serviceTypeId" in parsed)) return undefined;

  return typeof parsed.serviceTypeId === "string" ? parsed.serviceTypeId : undefined;
};

const reportsAreEqual = (a: AnswerForType["Bridge Eligibility"], b: AnswerForType["Bridge Eligibility"]): boolean => {
  if (a?.userIds?.length !== b?.userIds?.length) return false;
  if (!a?.userIds?.every((id) => b?.userIds?.includes(id))) return false;
  if (a.payerId !== b?.payerId) return false;
  if (a.status !== b?.status) return false;
  return true;
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return undefined;
};

interface UseBridgeEligibilityCheckArgs {
  value: AnswerForType["Bridge Eligibility"];
  onChange: (newVal: AnswerForType["Bridge Eligibility"]) => void;
  onError?: (serviceTypeId: string) => void;
  enduserId: string;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  priorResponses: TellescopeFieldResponse[];
  serviceTypeIds: string[];
  bridgeEligibilityType: "Hard" | "Soft" | undefined;
  enabled?: boolean;
  sessionType?: "enduser" | "user";
}

export const useBridgeEligibilityCheck = ({
  value,
  onChange,
  onError,
  enduserId,
  serviceTypeIds,
  priorResponses,
  enduser,
  bridgeEligibilityType = "Soft",
  enabled = true,
  sessionType = "enduser",
}: UseBridgeEligibilityCheckArgs) => {
  const latestInsuranceAnswer = getLatestInsuranceAnswer(priorResponses);
  const latestAddressAnswer = getLatestAddressAnswer(priorResponses);
  const payerId = latestInsuranceAnswer?.payerId;
  const memberId = latestInsuranceAnswer?.memberId;
  const state = latestAddressAnswer?.state || enduser?.state;
  // Staff sessions never auto-run — a paid Hard check must not fire without an explicit click.
  const [staffCheckType, setStaffCheckType] = useState<"Soft" | "Hard">();

  const getStringForHash = useCallback(
    () =>
      JSON.stringify({
        serviceTypeIds: [...serviceTypeIds].sort(),
        payerId,
        memberId,
        state,
        bridgeEligibilityType,
        // Without this, a staff Soft->Hard re-check hashes identically to the Soft run it follows and
        // the dedupe guard would swallow the fresh result.
        staffCheckType,
      }),
    [bridgeEligibilityType, memberId, payerId, serviceTypeIds, staffCheckType, state]
  );

  // Written from the side-effect callbacks, never during render, so a stale message cannot be latched
  // into a later run's result the way a render-time `??` assignment did.
  const [errorMessage, setErrorMessage] = useState<string>();

  const settledResultsRef = useRef<Map<string, BridgeServiceTypeResult>>(new Map());
  const reportedServiceTypeIdsWithErrors = useRef<Set<string>>(new Set<string>());

  /** A re-check must aggregate only its own responses, never those left behind by a prior run. */
  const startRun = useCallback((checkType: "Soft" | "Hard") => {
    settledResultsRef.current.clear();
    reportedServiceTypeIdsWithErrors.current.clear();
    setErrorMessage(undefined);
    setStaffCheckType(checkType);
  }, []);

  const triggerSoftCheck = useCallback(() => startRun("Soft"), [startRun]);
  const triggerHardCheck = useCallback(() => startRun("Hard"), [startRun]);

  const isConfigured = serviceTypeIds.length > 0;
  const activeCheckType = sessionType === "user" ? staffCheckType : bridgeEligibilityType;
  const isCached = !!(value?.status && value.payerId === payerId);
  // Staff re-check deliberately, so an explicit staff request overrides the cached answer.
  const canRun = isConfigured && enabled && !!activeCheckType && (!isCached || sessionType === "user");

  const lastReportRef = useRef<{ hash: number; reportedResults: AnswerForType["Bridge Eligibility"] }>(null);

  /**
   * Records one settled proxy response against its service type and, once every configured service
   * type has answered, reports the aggregate. Runs outside the render pass, so it is safe to call
   * `onChange` from here — unlike `combine`, which React invokes while rendering.
   */
  const reportSettledResponse = useCallback(
    (serviceTypeId: string, result: BridgeServiceTypeResult) => {
      settledResultsRef.current.set(serviceTypeId, result);
      if (settledResultsRef.current.size < serviceTypeIds.length) return;

      const aggregate = getAggregateResult(
        [...settledResultsRef.current].map(([id, result]) => ({ ...result, serviceTypeId: id }))
      );
      const resultValue = { payerId, userIds: aggregate.userIds, status: aggregate.status };

      const currentHash = hashString(getStringForHash());
      if (
        lastReportRef.current &&
        currentHash === lastReportRef.current.hash &&
        reportsAreEqual(lastReportRef.current.reportedResults, resultValue)
      ) {
        return;
      }

      lastReportRef.current = { hash: currentHash, reportedResults: resultValue };
      onChange(resultValue);
    },
    [getStringForHash, onChange, payerId, serviceTypeIds.length]
  );

  /**
   * Reports a failed service type once. Callable from outside the render pass, so the ref guard is not
   * corrupted by a discarded StrictMode render.
   */
  const reportServiceTypeError = useCallback(
    (serviceTypeId: string, error?: unknown) => {
      // A failed check still counts as settled, so one failure cannot stall the aggregate forever.
      reportSettledResponse(serviceTypeId, { status: "error", userIds: [] });
      setErrorMessage(getErrorMessage(error));
      if (reportedServiceTypeIdsWithErrors.current.has(serviceTypeId)) return;
      reportedServiceTypeIdsWithErrors.current.add(serviceTypeId);
      onError?.(serviceTypeId);
    },
    [onError, reportSettledResponse]
  );

  const combineResults = useCallback(
    (
      queries: {
        isFetched: boolean;
        isError: boolean;
        error?: unknown;
        data?: { data?: unknown };
      }[],
      pendingPhase: BridgeEligibilityPhase,
      isActive: boolean
    ): BridgeResult => {
      const allFetched = isActive && queries.length > 0 && queries.every((query) => query.isFetched);

      const typeCheckedResults = queries.reduce<BridgeServiceTypeResultWithId[]>((acc, query, i) => {
        const serviceTypeId = serviceTypeIds[i];
        if (!serviceTypeId) return acc;

        if (query.error) {
          acc.push({ serviceTypeId, status: "error", userIds: [] });
          return acc;
        }

        const checkResult = extractCheckResult(query.data?.data);
        if (checkResult) acc.push({ serviceTypeId, ...checkResult });

        return acc;
      }, []);

      const result: BridgeResult = {
        phase: allFetched ? "result" : pendingPhase,
        aggregateResult: getAggregateResult(typeCheckedResults),
      };
      return result;
    },
    [serviceTypeIds]
  );

  const softResult = useTellescopeQueries({
    model: "integrations",
    method: "proxy_read",
    requests: serviceTypeIds.map(
      (serviceTypeId): ProxyReadRequest => ({
        id: enduserId,
        integration: BRIDGE_TITLE,
        type: "provider-eligibility",
        query: JSON.stringify({ serviceTypeId, payerId, state }),
      })
    ),
    enabled: canRun && activeCheckType === "Soft",
    responseSideEffect: (request, response) => {
      const serviceTypeId = getRequestedServiceTypeId(request);
      if (serviceTypeId) reportSettledResponse(serviceTypeId, extractCheckResult(response?.data) ?? { userIds: [] });
    },
    errorSideEffect: (request, error) => {
      const serviceTypeId = getRequestedServiceTypeId(request);
      if (serviceTypeId) reportServiceTypeError(serviceTypeId, error);
    },
    combine: (queries) => combineResults(queries, "loading-soft", canRun && activeCheckType === "Soft"),
  });

  const serviceEligibilityIdMap = useTellescopeQueries({
    model: "integrations",
    method: "proxy_read",
    requests: serviceTypeIds.map(
      (serviceTypeId): ProxyReadRequest => ({
        id: enduserId,
        integration: BRIDGE_TITLE,
        type: "service-eligibility",
        query: JSON.stringify({ serviceTypeId, payerId, memberId, state }),
      })
    ),
    enabled: canRun && activeCheckType === "Hard",
    ...PAID_CHECK_QUERY_OPTIONS,
    errorSideEffect: (request, error) => {
      const serviceTypeId = getRequestedServiceTypeId(request);
      if (serviceTypeId) reportServiceTypeError(serviceTypeId, error);
    },
    combine: (queries) => ({
      isPending: queries.some((query) => query.status === "pending"),
      initFailed: queries.every((query) => query.isFetched || query.isError) && queries.every((query) => !query.data),
      idMap: createKeyMap(
        queries.reduce<{ serviceTypeId: string; serviceEligibilityId: string }[]>((acc, query, i) => {
          const serviceTypeId = serviceTypeIds[i];
          const serviceEligibilityId =
            typeof query.data?.data === "object" &&
            query.data.data !== null &&
            "id" in query.data.data &&
            typeof query.data.data.id === "string"
              ? query.data?.data.id
              : undefined;

          if (serviceEligibilityId && serviceTypeId) {
            acc.push({
              serviceTypeId,
              serviceEligibilityId,
            });
          }

          return acc;
        }, []),
        "serviceEligibilityId"
      ),
    }),
  });

  const eligibilityIds = Object.keys(serviceEligibilityIdMap.idMap);
  const canPollHard = eligibilityIds.length > 0;
  // Poll requests are keyed by eligibility id, so attribution goes back through the id map.
  const polledServiceTypeIdFor = (request: ProxyReadRequest) =>
    request.id ? serviceEligibilityIdMap.idMap[request.id]?.serviceTypeId : undefined;
  const hardResult = useTellescopeQueries({
    model: "integrations",
    method: "proxy_read",
    requests: eligibilityIds.map(
      (serviceEligibilityId): ProxyReadRequest => ({
        integration: BRIDGE_TITLE,
        type: "service-eligibility-poll",
        id: serviceEligibilityId,
      })
    ),
    enabled: canRun && canPollHard && activeCheckType === "Hard",
    ...PAID_CHECK_QUERY_OPTIONS,
    // The payer answers a hard check asynchronously, so keep polling while the status is pending.
    // Polling a queued result is not a new billed check — the charge was incurred on initiation.
    refetchInterval: (query) =>
      isPendingPollStatus(extractCheckResult(query.state.data?.data)?.status) ? POLL_INTERVAL_MS : false,
    responseSideEffect: (request, response) => {
      const serviceTypeId = polledServiceTypeIdFor(request);
      if (!serviceTypeId) return;
      const result = extractCheckResult(response?.data);
      if (isPendingPollStatus(result?.status)) return;
      reportSettledResponse(serviceTypeId, result ?? { userIds: [] });
    },
    errorSideEffect: (request, error) => {
      const serviceTypeId = polledServiceTypeIdFor(request);
      if (serviceTypeId) reportServiceTypeError(serviceTypeId, error);
    },
    combine: (queries) => combineResults(queries, "polling-hard", canRun && canPollHard && activeCheckType === "Hard"),
  });

  const baseResult = ((): BridgeResult => {
    if (!isConfigured) {
      return { phase: "not-configured", aggregateResult: { status: undefined, userIds: [] } };
    }
    if (!activeCheckType) {
      return { phase: "idle", aggregateResult: { status: undefined, userIds: [] } };
    }
    if (activeCheckType === "Soft") return softResult;
    if (canPollHard) return hardResult;
    if (serviceEligibilityIdMap.initFailed && !serviceEligibilityIdMap.isPending) {
      return { phase: "error", aggregateResult: { status: undefined, userIds: [] } };
    }
    return { phase: "polling-hard", aggregateResult: { status: "", userIds: [] } };
  })();

  return {
    ...baseResult,
    payerId,
    memberId,
    state,
    errorMessage,
    isChecking: baseResult.phase === "loading-soft" || baseResult.phase === "polling-hard",
    triggerSoftCheck,
    triggerHardCheck,
  };
};
